import { useEffect, useState, useCallback } from "react";
import { fetchItem, markItemRead } from "../api/items";
import { TagBadge } from "./TagBadge";
import { ScoreRing } from "./ScoreRing";
import { Accordion } from "./Accordion";
import type { ItemDetail } from "../types";

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("de-DE", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatBytes(bytes: number | null): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(mime: string | null): string {
  if (!mime) return "📄";
  if (mime.includes("pdf")) return "📕";
  if (mime.includes("word") || mime.includes("doc")) return "📘";
  if (mime.includes("sheet") || mime.includes("xls")) return "📗";
  if (mime.includes("image")) return "🖼️";
  return "📄";
}

function cleanAuthor(author: string | null): string {
  if (!author) return "Unbekannt";
  const match = author.match(/"?([^"<]+)"?\s*<?/);
  return match?.[1]?.trim() || author;
}

function parseSummaryText(raw: string): string[] {
  // 1) Try JSON parse — handles ["- item1", "- item2"] or ["item1", "item2"]
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.map((s: unknown) => String(s).trim()).filter(Boolean);
    }
    if (typeof parsed === "string") {
      return parsed.split("\n").map((s) => s.trim()).filter(Boolean);
    }
  } catch {
    // Not JSON — continue with string cleanup
  }

  // 2) Strip leftover JSON artifacts: {" ... "} or [" ... "]
  let cleaned = raw;
  if (/^\s*[\[{]/.test(cleaned) && /[\]}]\s*$/.test(cleaned)) {
    cleaned = cleaned
      .replace(/^\s*[\[{]\s*/, "")
      .replace(/\s*[\]}]\s*$/, "")
      .replace(/",\s*"/g, "\n")
      .replace(/^"|"$/g, "");
  }

  return cleaned.split("\n").map((s) => s.trim()).filter(Boolean);
}

function renderSummary(summary: string | null): React.ReactNode {
  if (!summary) return <p className="text-muted">Keine KI-Zusammenfassung verfügbar.</p>;

  const lines = parseSummaryText(summary);
  const bullets: React.ReactNode[] = [];
  const paragraphs: React.ReactNode[] = [];

  lines.forEach((line, i) => {
    // Strip leading bullet chars
    const isBullet = /^[-*•]\s/.test(line);
    const text = line.replace(/^[-*•]\s*/, "");
    if (isBullet || lines.length > 1) {
      bullets.push(<li key={i}>{text}</li>);
    } else {
      paragraphs.push(<p key={i}>{text}</p>);
    }
  });

  return (
    <>
      {paragraphs}
      {bullets.length > 0 && <ul>{bullets}</ul>}
    </>
  );
}

interface ArticleDrawerProps {
  itemId: number;
  onClose: () => void;
}

export function ArticleDrawer({ itemId, onClose }: ArticleDrawerProps) {
  const [item, setItem] = useState<ItemDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showOriginal, setShowOriginal] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchItem(itemId)
      .then((data) => {
        setItem(data);
        if (!data.is_read) {
          markItemRead(data.id, true).catch(() => {});
        }
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Fehler"))
      .finally(() => setLoading(false));
  }, [itemId]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  const latestAnnotation = item?.annotations[0];
  const tags = latestAnnotation?.tags_json ? JSON.parse(latestAnnotation.tags_json) : [];
  const score = latestAnnotation?.relevance_score;
  const hasTranslation = !!(latestAnnotation?.translated_title || latestAnnotation?.translated_content);
  const isNonGerman = item?.language && !["de", "deu"].includes(item.language.toLowerCase());

  return (
    <>
      <div className="drawer-overlay" onClick={handleOverlayClick} />
      <div className="drawer">
        <div className="drawer-header">
          <span className="drawer-header-label">Artikel-Details</span>
          <button className="drawer-close" onClick={onClose} title="Schließen">✕</button>
        </div>

        <div className="drawer-body">
          {loading && <p className="loading-text">Lade Artikel...</p>}
          {error && <p className="error-text">Fehler: {error}</p>}

          {item && (
            <>
              {/* Tags */}
              {tags.length > 0 && (
                <div className="drawer-tags">
                  {tags.map((t: string) => <TagBadge key={t} tag={t} />)}
                </div>
              )}

              {/* Translation Toggle */}
              {hasTranslation && isNonGerman && (
                <div className="drawer-lang-toggle">
                  <button
                    className={`lang-toggle-btn ${!showOriginal ? "lang-toggle-active" : ""}`}
                    onClick={() => setShowOriginal(false)}
                  >
                    🇩🇪 Deutsch
                  </button>
                  <button
                    className={`lang-toggle-btn ${showOriginal ? "lang-toggle-active" : ""}`}
                    onClick={() => setShowOriginal(true)}
                  >
                    {item.language?.toUpperCase() === "EN" ? "🇬🇧" : "🌐"} Original ({item.language?.toUpperCase()})
                  </button>
                </div>
              )}

              {/* Title + Score */}
              <div className="drawer-title-row">
                <h1 className="drawer-title">
                  {hasTranslation && !showOriginal && latestAnnotation?.translated_title
                    ? latestAnnotation.translated_title
                    : (item.title || "Ohne Titel")}
                </h1>
                {score !== null && score !== undefined && (
                  <div className="drawer-title-score">
                    <ScoreRing score={score} size={44} />
                  </div>
                )}
              </div>

              {/* Meta */}
              <div className="drawer-meta">
                <span>Von: {cleanAuthor(item.author)}</span>
                <span className="drawer-meta-sep">|</span>
                <span>{formatDate(item.published_at)}</span>
                {item.language && (
                  <>
                    <span className="drawer-meta-sep">|</span>
                    <span>{item.language.toUpperCase()}</span>
                  </>
                )}
                {hasTranslation && !showOriginal && (
                  <>
                    <span className="drawer-meta-sep">|</span>
                    <span className="translation-badge">KI-Übersetzung</span>
                  </>
                )}
              </div>

              {/* Warum relevant? (einklappbar, über der Zusammenfassung) */}
              {latestAnnotation?.rationale && (
                <Accordion title="Warum relevant?" defaultOpen>
                  <div className="drawer-rationale">
                    <p>{latestAnnotation.rationale}</p>
                  </div>
                </Accordion>
              )}

              {/* KI-Zusammenfassung */}
              <div className="drawer-section" style={{ marginTop: 20 }}>
                <h3>KI-Zusammenfassung</h3>
                <div className="drawer-summary">
                  {renderSummary(latestAnnotation?.summary || null)}
                </div>
              </div>

              {/* Anhaenge */}
              {item.attachments.length > 0 && (
                <div className="drawer-section">
                  <h3>Anhänge ({item.attachments.length})</h3>
                  {item.attachments.map((a) => (
                    <a
                      key={a.id}
                      href={`/api/items/${item.id}/attachments/${a.id}/download`}
                      className="drawer-attachment"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="drawer-attachment-icon">{getFileIcon(a.mime_type)}</span>
                      <span className="drawer-attachment-name">{a.filename || "Datei"}</span>
                      {a.size && <span className="drawer-attachment-size">{formatBytes(a.size)}</span>}
                    </a>
                  ))}
                </div>
              )}

              {/* Übersetzter Inhalt (wenn vorhanden und DE-Modus aktiv) */}
              {hasTranslation && !showOriginal && latestAnnotation?.translated_content && (
                <Accordion title="Übersetzter Inhalt" defaultOpen>
                  <div className="article-text-content translated-content">
                    {latestAnnotation.translated_content}
                  </div>
                </Accordion>
              )}

              {/* Original-Inhalt */}
              <Accordion title={hasTranslation ? `Original-Inhalt (${item.language?.toUpperCase() || "?"})` : "Original-Inhalt anzeigen"} defaultOpen={showOriginal || !hasTranslation}>
                {item.content_html_sanitized ? (
                  <div
                    className="article-html-content"
                    dangerouslySetInnerHTML={{ __html: item.content_html_sanitized }}
                  />
                ) : (
                  <pre className="article-text-content">{item.content_text || "Kein Inhalt"}</pre>
                )}
              </Accordion>

              {/* Metadaten + Links (einklappbar, standardmäßig geschlossen) */}
              <Accordion title="Metadaten">
                <dl className="meta-list">
                  <dt>Absender</dt>
                  <dd>{item.author || "Unbekannt"}</dd>
                  <dt>Eingang</dt>
                  <dd>{formatDate(item.ingested_at)}</dd>
                  <dt>Status</dt>
                  <dd><span className={`status-badge status-${item.status}`}>{item.status}</span></dd>
                  {item.external_id && (
                    <>
                      <dt>ID</dt>
                      <dd className="meta-mono">{item.external_id}</dd>
                    </>
                  )}
                </dl>

                {/* Links (dedupliziert) */}
                {(() => {
                  const seen = new Set<string>();
                  const unique = item.links.filter((l) => {
                    const norm = l.url.replace(/[?#].*$/, "").replace(/\/+$/, "");
                    if (seen.has(norm)) return false;
                    seen.add(norm);
                    return true;
                  });
                  if (unique.length === 0) return null;
                  return (
                    <>
                      <h4 className="drawer-meta-subtitle">Links ({unique.length})</h4>
                      <ul className="drawer-links">
                        {unique.map((l) => (
                          <li key={l.id}>
                            <a href={l.url} target="_blank" rel="noopener noreferrer" title={l.url}
                               onClick={(e) => e.stopPropagation()}>
                              {l.domain || l.url}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </>
                  );
                })()}
              </Accordion>
            </>
          )}
        </div>
      </div>
    </>
  );
}
